/**
 * Utility functions for frequency conversions and calculations
 * Extracted for testability
 */

// Logarithmic scale constants
export const MIN_LOG_FREQ = Math.log(20); // Log of 20 Hz
export const MAX_LOG_FREQ = Math.log(24000); // Log of 24000 Hz
export const LOG_RANGE = MAX_LOG_FREQ - MIN_LOG_FREQ;

/**
 * Convert logarithmic value to linear (0-1 range)
 */
export function logToLinear(logValue) {
  return (logValue - MIN_LOG_FREQ) / LOG_RANGE;
}

/**
 * Convert linear value (0-1 range) to logarithmic
 */
export function linearToLog(linearValue) {
  return MIN_LOG_FREQ + (linearValue * LOG_RANGE);
}

/**
 * Convert logarithmic value to frequency in Hz
 */
export function logToFrequency(logValue) {
  return Math.exp(logValue);
}

/**
 * Convert frequency in Hz to logarithmic value
 */
export function frequencyToLog(frequency) {
  return Math.log(frequency);
}

/**
 * Get frequency from Y coordinate on canvas
 * @param {number} y - Y coordinate
 * @param {number} canvasHeight - Canvas height
 * @param {number} minFrequency - Minimum frequency
 * @param {number} maxFrequency - Maximum frequency
 * @param {boolean} isLogScale - Whether to use logarithmic scale
 * @returns {number} Frequency in Hz
 */
export function getFrequencyFromY(y, canvasHeight, minFrequency, maxFrequency, isLogScale) {
  let freq;
  if (isLogScale) {
    const logMin = frequencyToLog(minFrequency);
    const logMax = frequencyToLog(maxFrequency);
    const logFreq = logMin + ((canvasHeight - 1 - y) / (canvasHeight - 1)) * (logMax - logMin);
    freq = logToFrequency(logFreq);
  } else {
    const linearMin = minFrequency;
    const linearMax = maxFrequency;
    freq = linearMin + ((canvasHeight - 1 - y) / (canvasHeight - 1)) * (linearMax - linearMin);
  }
  return freq;
}

/**
 * Get time offset from X coordinate on canvas
 * @param {number} x - X coordinate
 * @param {number} canvasWidth - Canvas width
 * @param {number} totalTimeSpan - Total time span in seconds
 * @returns {number} Time offset in seconds
 */
export function getTimeOffsetFromX(x, canvasWidth, totalTimeSpan) {
  if (canvasWidth <= 0) {
    return 0;
  }
  const clampedX = Math.min(Math.max(x, 0), canvasWidth);
  const fractionFromRight = (canvasWidth - clampedX) / canvasWidth;
  return fractionFromRight * totalTimeSpan;
}

/**
 * Inverse of getFrequencyFromY: convert a frequency in Hz to the Y pixel
 * row on the spectrogram. Returns the Y such that
 *   getFrequencyFromY(getYFromFrequency(f, ...), ...) ≈ f
 * on both the log and linear scales.
 *
 * @param {number} frequency - Frequency in Hz.
 * @param {number} canvasHeight - Canvas height in pixels.
 * @param {number} minFrequency - Lower bound of the visible range (Hz).
 * @param {number} maxFrequency - Upper bound of the visible range (Hz).
 * @param {boolean} isLogScale - Whether to use logarithmic scale.
 * @returns {number} Y coordinate; 0 for `maxFrequency`, `canvasHeight-1`
 *   for `minFrequency`. Values outside the range extrapolate linearly.
 */
export function getYFromFrequency(frequency, canvasHeight, minFrequency, maxFrequency, isLogScale) {
  if (canvasHeight <= 1) return 0;
  const h = canvasHeight - 1;
  if (isLogScale) {
    const logMin = frequencyToLog(minFrequency);
    const logMax = frequencyToLog(maxFrequency);
    if (logMax === logMin) return 0;
    const logFreq = frequencyToLog(frequency);
    return h - h * (logFreq - logMin) / (logMax - logMin);
  }
  if (maxFrequency === minFrequency) return 0;
  return h - h * (frequency - minFrequency) / (maxFrequency - minFrequency);
}

/**
 * Window function implementations
 */
export const windowFunctions = {
  rectangular: (buffer) => {
    return buffer;
  },
  hann: (buffer) => {
    const N = buffer.length;
    const windowedBuffer = new Float32Array(N);
    for (let i = 0; i < N; i++) {
      windowedBuffer[i] = buffer[i] * 0.5 * (1 - Math.cos((2 * Math.PI * i) / (N - 1)));
    }
    return windowedBuffer;
  },
  hamming: (buffer) => {
    const N = buffer.length;
    const windowedBuffer = new Float32Array(N);
    for (let i = 0; i < N; i++) {
      windowedBuffer[i] = buffer[i] * (0.54 - 0.46 * Math.cos((2 * Math.PI * i) / (N - 1)));
    }
    return windowedBuffer;
  },
  blackman: (buffer) => {
    const N = buffer.length;
    const windowedBuffer = new Float32Array(N);
    const alpha = 0.16;
    const a0 = (1 - alpha) / 2;
    const a1 = 0.5;
    const a2 = alpha / 2;
    for (let i = 0; i < N; i++) {
      windowedBuffer[i] = buffer[i] * (a0 - a1 * Math.cos((2 * Math.PI * i) / (N - 1)) + a2 * Math.cos((4 * Math.PI * i) / (N - 1)));
    }
    return windowedBuffer;
  }
};

/**
 * Color scheme functions
 */
export const colorSchemes = {
  rainbow: (value) => {
    const hue = 240 - (value / 255) * 240;
    return `hsl(${hue}, 100%, 50%)`;
  },
  'green-orange-red': (value) => {
    let r = 0, g = 0, b = 0;
    const n = value / 255; // Normalized value from 0 to 1

    if (n < 0.33) { // Black to Forest Green
      // Interpolate R, G, B from (0,0,0) to (34,139,34)
      r = Math.floor(n / 0.33 * 34);
      g = Math.floor(n / 0.33 * 139);
      b = Math.floor(n / 0.33 * 34);
    } else if (n < 0.66) { // Forest Green to Orange
      const n_phase = (n - 0.33) / 0.33;
      // Interpolate R from 34 to 255
      r = Math.floor(34 + n_phase * (255 - 34));
      // Interpolate G from 139 to 165
      g = Math.floor(139 + n_phase * (165 - 139));
      // Interpolate B from 34 to 0
      b = Math.floor(34 + n_phase * (0 - 34));
    } else { // Orange to Red
      const n_phase = (n - 0.66) / 0.34;
      // Interpolate R from 255 to 255 (stays 255)
      r = 255;
      // Interpolate G from 165 to 0
      g = Math.floor(165 + n_phase * (0 - 165));
      // Interpolate B from 0 to 0 (stays 0)
      b = 0;
    }
    return `rgb(${r}, ${g}, ${b})`;
  },
  grayscale: (value) => {
    const gray = Math.floor(value);
    return `rgb(${gray}, ${gray}, ${gray})`;
  }
};

/**
 * Convert webfft interleaved output to phasor format
 * @param {Float32Array} interleavedArray - Interleaved real/imaginary values
 * @returns {Array<Array<number>>} Array of [real, imag] pairs
 */
export function convertWebfftOutput(interleavedArray) {
  const result = [];
  // For real FFT, we only use the first half (positive frequencies)
  // webfft.fftr() already returns only positive frequencies
  // Reverse the order to match the original frequency scale
  for (let i = interleavedArray.length - 2; i >= 0; i -= 2) {
    result.push([interleavedArray[i], interleavedArray[i + 1]]);
  }
  return result;
}

/**
 * Pick the vertical gain for the next waveform frame.
 *
 * The goal is that successive frames overlap visually: when the signal is
 * steady, the gain should stay stable; when the signal quiets after a spike,
 * the gain should release promptly; and the trace must never clip outside
 * its drawable area.
 *
 * The target is derived from the *current* frame only — it's the larger of
 * the peak magnitude and √2·RMS (the peak of a sine with the same RMS).
 * Both quantities are invariant to how the cross-correlation trigger lines
 * up the window sample-for-sample, so clean tones don't flatten under
 * sub-sample phase drift. Using an absolute target (not a ratio against the
 * previous gain) also means a loud spike can't pin the gain high after the
 * signal quiets: the next frame's target drops with the signal.
 *
 * Smoothing toward the target uses fast attack and moderate decay; the hard
 * peak ceiling at the end guarantees the trace never clips even when a
 * sudden transient arrives faster than the smoothing can follow.
 *
 * `previousWaveform` and `previousGainLevel` are accepted for API symmetry
 * with the caller's existing state but are intentionally not used for gain
 * selection — coupling the new gain to the previous gain is what caused
 * both the runaway-flatten-on-clean-tone and the stuck-high-after-spike
 * failure modes in earlier revisions.
 *
 * @param {Float32Array|Array<number>} samples - The window that will be drawn.
 * @param {Float32Array|Array<number>|null} previousWaveform - The window that
 *   was drawn in the previous frame (unused; kept for API compatibility).
 * @param {number} previousGainLevel - The gain applied to `previousWaveform`
 *   (unused; kept for API compatibility).
 * @param {number} currentGain - The gain value from the previous call, used
 *   as the smoothing starting point.
 * @returns {{gain: number, frameMax: number}} The new gain to apply and the
 *   peak magnitude of `samples` (cached for the caller).
 */
/**
 * Detect a sustained exact-zero audio stream.
 *
 * When the OS briefly routes the microphone away from the browser (for
 * example because the user switched to another app), the AudioWorklet
 * keeps firing but every sample is literally 0 — real mic silence always
 * has some tiny non-zero noise, so exact zeros are a reliable "stream is
 * stuck" signal. This detector returns `true` once `thresholdSamples`
 * consecutive zero samples have been observed across one or more buffers,
 * so the caller can trigger an audio-context restart.
 *
 * @param {{thresholdSamples: number}} options - Number of consecutive
 *   all-zero samples required to declare a flatline. Typically the sample
 *   rate for a one-second threshold.
 * @returns {{observe: (buffer: Float32Array|Array<number>) => boolean,
 *            reset: () => void, count: () => number}}
 *   observe() returns true exactly when the threshold has been crossed by
 *   the current buffer — treat it as a level signal the caller latches.
 *   reset() clears the counter. count() returns the current consecutive
 *   zero-sample count for diagnostics.
 */
export function createFlatlineDetector({ thresholdSamples }) {
  let consecutive = 0;
  return {
    observe(buffer) {
      let allZero = true;
      for (let i = 0; i < buffer.length; i++) {
        if (buffer[i] !== 0) { allZero = false; break; }
      }
      if (allZero) {
        consecutive += buffer.length;
        return consecutive >= thresholdSamples;
      }
      consecutive = 0;
      return false;
    },
    reset() { consecutive = 0; },
    count() { return consecutive; },
  };
}

/**
 * Convert a frequency in Hz to the nearest equal-tempered note name (A4=440)
 * and the deviation in cents (-50..+50).
 *
 * Returns `null` for non-positive or non-finite inputs so callers can skip
 * rendering gracefully when the frequency is out of range.
 *
 * @param {number} frequency - Frequency in hertz.
 * @returns {{name: string, cents: number}|null}
 */
const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
export function frequencyToNote(frequency) {
  if (!Number.isFinite(frequency) || frequency <= 0) return null;
  const midi = 69 + 12 * Math.log2(frequency / 440);
  const nearest = Math.round(midi);
  // `|| 0` normalises -0 to 0 so a clean match displays "0¢", not "-0¢".
  const cents = Math.round((midi - nearest) * 100) || 0;
  const name = NOTE_NAMES[((nearest % 12) + 12) % 12];
  const octave = Math.floor(nearest / 12) - 1;
  return { name: `${name}${octave}`, cents };
}

import { PitchDetector } from 'pitchy';

/**
 * Pick the largest font size from `sizes` (ordered largest first) such that
 * the resulting label box fits within `availableWidth`. Falls back to the
 * smallest size when nothing fits, so the caller always gets *some* label.
 *
 * `measureTextWidth(size)` returns the rendered text width in pixels at the
 * given size; the caller owns font selection. `paddingWidth` is the horizontal
 * padding added around the text.
 *
 * @param {number} availableWidth
 * @param {Array<number>} sizes - candidate font sizes, largest first
 * @param {(size: number) => number} measureTextWidth
 * @param {number} paddingWidth
 * @returns {number}
 */
export function pickLabelFontSize(availableWidth, sizes, measureTextWidth, paddingWidth) {
  if (sizes.length === 0) {
    throw new Error('pickLabelFontSize requires at least one candidate size');
  }
  for (const size of sizes) {
    if (measureTextWidth(size) + paddingWidth <= availableWidth) return size;
  }
  return sizes[sizes.length - 1];
}

// Cache PitchDetector instances by input length. Constructing one allocates
// internal FFT buffers, so we reuse across frames — drawWaveform reads a
// different size depending on zoom, and the spectrogram pitch tracker uses
// a fixed 2048-sample analysis window.
const pitchDetectorCache = new Map();
function getPitchDetector(size) {
  let d = pitchDetectorCache.get(size);
  if (!d) {
    d = PitchDetector.forFloat32Array(size);
    pitchDetectorCache.set(size, d);
  }
  return d;
}

/**
 * Detect the fundamental frequency of `samples` in Hz using Pitchy's McLeod
 * Pitch Method. Returns `null` when pitchy reports an invalid pitch or the
 * clarity score falls below `clarityThreshold` (0..1, default 0.9 — pitchy's
 * own recommended threshold for confident detection).
 *
 * @param {Float32Array|Array<number>} samples
 * @param {{sampleRate: number, clarityThreshold?: number}} opts
 * @returns {number|null}
 */
export function detectPitchHz(samples, opts) {
  const { sampleRate, clarityThreshold = 0.9 } = opts;
  if (!samples.length) return null;
  const detector = getPitchDetector(samples.length);
  const [pitch, clarity] = detector.findPitch(samples, sampleRate);
  if (!Number.isFinite(pitch) || pitch <= 0) return null;
  if (clarity < clarityThreshold) return null;
  return pitch;
}

/**
 * Least-squares phase φ of a sine wave at angular frequency ω (radians per
 * sample) fitting `samples[i] ≈ A · sin(ω·i + φ)`. Returns φ in radians in
 * the range (-π, π], or `null` for empty input.
 *
 * @param {Float32Array|Array<number>} samples
 * @param {number} omega
 * @returns {number|null}
 */
export function fitSinePhase(samples, omega) {
  const n = samples.length;
  if (n === 0) return null;
  let a = 0, b = 0;
  for (let i = 0; i < n; i++) {
    a += samples[i] * Math.sin(omega * i);
    b += samples[i] * Math.cos(omega * i);
  }
  return Math.atan2(b, a);
}

export function chooseWaveformGain(samples, previousWaveform, previousGainLevel, currentGain) {
  const n = samples.length;

  let frameMax = 0;
  let fullEnergy = 0;
  for (let i = 0; i < n; i++) {
    const s = samples[i];
    const abs = s < 0 ? -s : s;
    if (abs > frameMax) frameMax = abs;
    fullEnergy += s * s;
  }

  const rmsPeakEstimate = n > 0 ? Math.sqrt(fullEnergy / n) * Math.SQRT2 : 0;
  const targetGain = frameMax > rmsPeakEstimate ? frameMax : rmsPeakEstimate;

  const rate = targetGain > currentGain ? 0.3 : 0.2;
  let newGain = currentGain + (targetGain - currentGain) * rate;

  if (frameMax > newGain) newGain = frameMax;

  return { gain: newGain, frameMax };
}
